import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Wrench, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cardStyles } from "@/lib/status-styles";
import { formatNumber } from "@/lib/formatting-utils.js";
import DowntimeTypeBadge from "./downtime-type-badge.jsx";

/**
 * Top Downtime Reasons Table Component
 * Displays top 10 downtime reasons with filtering by selected month
 * @param {Object} props
 * @param {Array} props.topReasons - Array of top downtime reasons
 * @param {string|null} props.selectedMonth - Currently selected month for filtering
 * @param {boolean} props.isLoading - Loading state
 * @param {string|null} props.error - Error message
 * @returns {JSX.Element} Top Downtime Reasons Table component
 */
export default function TopDowntimeReasonsTable({
  topReasons,
  selectedMonth,
  isLoading,
  error,
}) {
  return (
    <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden'>
      <CardHeader className='py-3 px-4 border-b border-border'>
        <div className='flex items-center gap-2'>
          <Wrench className='h-5 w-5 text-muted-foreground' />
          <CardTitle className='text-base font-medium text-foreground'>
            Top Downtime Reasons
          </CardTitle>
          {selectedMonth && (
            <Badge
              variant='outline'
              className='bg-primary/10 text-primary border-primary/30'>
              {selectedMonth}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className={cardStyles.content}>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
            <span className='ml-3 text-muted-foreground'>
              Loading downtime reasons...
            </span>
          </div>
        ) : error ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              <AlertTriangle className='h-8 w-8 text-red-500 mx-auto mb-2' />
              <p className='text-red-600 mb-2'>{error}</p>
            </div>
          </div>
        ) : topReasons.length === 0 ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              {selectedMonth && (
                <p className='text-muted-foreground mb-2'>
                  No downtime in selected month
                </p>
              )}
              {!selectedMonth && (
                <p className='text-muted-foreground mb-2'>
                  No downtime events found
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className={cardStyles.tableContainer}>
            <Table className='w-full'>
              <TableHeader className={cardStyles.tableHeader}>
                <TableRow className={cardStyles.tableRow}>
                  <TableHead className={`${cardStyles.tableHead} py-1.5`}>
                    #
                  </TableHead>
                  <TableHead className={`${cardStyles.tableHead} py-1.5`}>
                    Reason
                  </TableHead>
                  <TableHead className={`${cardStyles.tableHead} py-1.5`}>
                    Type
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Events
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Downtime
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topReasons.map((item, index) => (
                  <TableRow
                    key={`${item.reason}-${index}`}
                    className={cardStyles.bodyRow}>
                    <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                      {index + 1}
                    </TableCell>
                    <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                      {item.reason}
                    </TableCell>
                    <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                      <DowntimeTypeBadge type={item.type} />
                    </TableCell>
                    <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
                      <span className='text-sm font-mono font-light'>
                        {item.eventCount}
                      </span>
                    </TableCell>
                    <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
                      <span className='text-sm font-mono font-light'>
                        {formatNumber(item.totalDowntimeHours, 2)} h
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
